import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usersApi } from '@/services/users'
import type {
  CreateUserRequest,
  ListQuery,
  PermissionMatrix,
  UpdateUserRequest,
  UserListQuery,
} from '@/types'

const KEY = 'users'

function useInvalidateList() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: [KEY, 'list'] })
}

// 2.11.1 - paginated/filtered user list.
export function useUsers(q: UserListQuery = {}) {
  return useQuery({
    queryKey: [KEY, 'list', q],
    queryFn: () => usersApi.list(q),
  })
}

// 2.11.2 - single user.
export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'detail', id],
    queryFn: () => usersApi.get(id as string),
    enabled: !!id,
  })
}

// 2.11.2 - create.
export function useCreateUser() {
  const invalidate = useInvalidateList()
  return useMutation({
    mutationFn: (body: CreateUserRequest) => usersApi.create(body),
    onSuccess: invalidate,
  })
}

// 2.11.2 - update.
export function useUpdateUser() {
  const qc = useQueryClient()
  const invalidate = useInvalidateList()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserRequest }) =>
      usersApi.update(id, body),
    onSuccess: (user, { id }) => {
      qc.setQueryData([KEY, 'detail', id], user)
      invalidate()
    },
  })
}

// 2.11.1/2.11.2 - (de)activate.
export function useSetUserActive() {
  const invalidate = useInvalidateList()
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      usersApi.setActive(id, isActive),
    onSuccess: invalidate,
  })
}

// 2.11.4 - soft delete + GDPR anonymise (Admin only).
export function useDeleteUser() {
  const invalidate = useInvalidateList()
  return useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: invalidate,
  })
}

// 2.11.3 - read module permission matrix for a user.
export function useUserPermissions(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'permissions', id],
    queryFn: () => usersApi.getPermissions(id as string),
    enabled: !!id,
  })
}

// 2.11.3 - persist module permission matrix.
export function useUpdateUserPermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      permissions,
    }: {
      id: string
      permissions: PermissionMatrix
    }) => usersApi.updatePermissions(id, permissions),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [KEY, 'permissions', vars.id] }),
  })
}

// 2.11.2 — resend welcome / credential email.
export function useResendUserWelcomeEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => usersApi.resendWelcomeEmail(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: [KEY, 'detail', id] })
    },
  })
}

// 2.11.2 — user document attachments.
export function useUserDocuments(id: string | undefined, q: ListQuery = {}) {
  return useQuery({
    queryKey: [KEY, id, 'documents', q],
    queryFn: () => usersApi.documents(id as string, q),
    enabled: !!id,
  })
}

export function useUploadUserDocument(userId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => usersApi.uploadDocument(userId as string, file),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [KEY, userId, 'documents'] }),
  })
}

export function useRenameUserDocument(userId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, name }: { documentId: string; name: string }) =>
      usersApi.renameDocument(userId as string, documentId, name),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [KEY, userId, 'documents'] }),
  })
}

export function useDeleteUserDocument(userId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) =>
      usersApi.deleteDocument(userId as string, documentId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [KEY, userId, 'documents'] }),
  })
}
